import React, { useState } from 'react';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FONT_FAMILY_OPTIONS,
  LHU_PAGE_FORMATS,
  STYLE_ENUM_OPTIONS,
  sanitizeStyleOverrideMap,
  type LhuPageFormat,
  type LhuPageStyleOverrides,
  type LhuStyleOverride,
  type LhuStyleOverrideKey,
  type LhuStyleOverrideMap,
  type LhuStyleTargetOnPage,
} from './lhuStyleOverrides';

export type LhuFormatTargets = {
  targets: LhuStyleTargetOnPage[];
  pageCount: number;
};

type Props = {
  open: boolean;
  initialFormat: LhuPageFormat;
  formats: Record<LhuPageFormat, LhuFormatTargets>;
  value: LhuStyleOverrideMap;
  onApply: (next: LhuStyleOverrideMap) => void;
  onClose: () => void;
};

type SelectColumn = {
  key: Exclude<LhuStyleOverrideKey, 'fontSize' | 'color'>;
  label: string;
  options: readonly { value: string; label: string }[];
  width: number;
};

const SELECT_COLUMNS: SelectColumn[] = [
  { key: 'fontFamily', label: 'Font', options: FONT_FAMILY_OPTIONS, width: 150 },
  { key: 'fontWeight', label: 'Tebal', options: STYLE_ENUM_OPTIONS.fontWeight, width: 110 },
  { key: 'fontStyle', label: 'Miring', options: STYLE_ENUM_OPTIONS.fontStyle, width: 110 },
  { key: 'textDecoration', label: 'Dekorasi', options: STYLE_ENUM_OPTIONS.textDecoration, width: 130 },
  { key: 'textTransform', label: 'Huruf', options: STYLE_ENUM_OPTIONS.textTransform, width: 140 },
  { key: 'textAlign', label: 'Rata', options: STYLE_ENUM_OPTIONS.textAlign, width: 140 },
];

function optionLabel(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

export function LhuStyleOverrideDialog({ open, initialFormat, formats, value, onApply, onClose }: Props) {
  const [draft, setDraft] = useState<LhuStyleOverrideMap>(() => value);
  const [format, setFormat] = useState<LhuPageFormat>(initialFormat);

  const { targets, pageCount } = formats[format];
  const page: LhuPageStyleOverrides = draft[format] ?? {};
  const overriddenCount = Object.keys(page).length;

  function updatePage(update: (prev: LhuPageStyleOverrides) => LhuPageStyleOverrides) {
    setDraft((prev) => {
      const nextPage = update(prev[format] ?? {});
      const out = { ...prev };
      if (Object.keys(nextPage).length) out[format] = nextPage;
      else delete out[format];
      return out;
    });
  }

  function setField(target: string, key: LhuStyleOverrideKey, next: string | number | undefined) {
    updatePage((prev) => {
      const current: LhuStyleOverride = { ...(prev[target] ?? {}) };
      if (next === undefined || next === '') delete current[key];
      else (current as Record<string, unknown>)[key] = next;
      const out = { ...prev };
      if (Object.keys(current).length) out[target] = current;
      else delete out[target];
      return out;
    });
  }

  function resetTarget(target: string) {
    updatePage((prev) => {
      const out = { ...prev };
      delete out[target];
      return out;
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="lhu-style-override-title"
      data-testid="lhu-style-override-dialog"
    >
      <DialogTitle id="lhu-style-override-title">
        Override styling
        <Typography variant="body2" color="text.secondary">
          Override berlaku untuk semua halaman dengan format yang sama. Kosongkan kolom untuk memakai default.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Format halaman"
            value={format}
            onChange={(e) => setFormat(e.target.value as LhuPageFormat)}
            sx={{ minWidth: 280 }}
            inputProps={{ 'data-testid': 'lhu-style-override-format' }}
          >
            {LHU_PAGE_FORMATS.map((f) => {
              const count = Object.keys(draft[f.key] ?? {}).length;
              return (
                <MenuItem key={f.key} value={f.key}>
                  {count > 0 ? `${f.label} (${count})` : f.label}
                </MenuItem>
              );
            })}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {pageCount > 0
              ? `Dipakai di ${pageCount} halaman pada LHU ini.`
              : 'Format ini tidak ada di LHU ini.'}
          </Typography>
        </Box>
        {targets.length === 0 ? (
          <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Tidak ada elemen yang bisa di-override untuk format ini.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderTop: 1, borderColor: 'divider' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200 }}>Elemen</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Ukuran (px)</TableCell>
                  {SELECT_COLUMNS.map((col) => (
                    <TableCell key={col.key} sx={{ minWidth: col.width }}>
                      {col.label}
                    </TableCell>
                  ))}
                  <TableCell sx={{ minWidth: 120 }}>Warna</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {targets.map((target) => {
                  const override = page[target.key] ?? {};
                  const overridden = Object.keys(override).length > 0;
                  return (
                    <TableRow
                      key={target.key}
                      data-testid={`lhu-style-row-${target.key}`}
                      sx={overridden ? { bgcolor: 'action.selected' } : undefined}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: overridden ? 600 : 400 }}>
                          {target.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Default {target.defaults.fontSize}px
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={override.fontSize ?? ''}
                          placeholder={String(target.defaults.fontSize)}
                          onChange={(e) =>
                            setField(
                              target.key,
                              'fontSize',
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                          inputProps={{ min: 4, max: 96, step: 0.5, 'aria-label': `Ukuran ${target.label}` }}
                          fullWidth
                        />
                      </TableCell>
                      {SELECT_COLUMNS.map((col) => (
                        <TableCell key={col.key}>
                          <TextField
                            select
                            size="small"
                            value={override[col.key] ?? ''}
                            onChange={(e) => setField(target.key, col.key, e.target.value)}
                            SelectProps={{ displayEmpty: true }}
                            inputProps={{ 'aria-label': `${col.label} ${target.label}` }}
                            fullWidth
                          >
                            <MenuItem value="">
                              <em>Default ({optionLabel(col.options, target.defaults[col.key])})</em>
                            </MenuItem>
                            {col.options.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box
                            component="input"
                            type="color"
                            value={override.color ?? target.defaults.color}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setField(target.key, 'color', e.target.value.toLowerCase())
                            }
                            aria-label={`Warna ${target.label}`}
                            sx={{ width: 36, height: 28, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
                          />
                          <Typography variant="caption" color={override.color ? 'text.primary' : 'text.secondary'}>
                            {override.color ?? 'Default'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Tooltip title="Reset ke default">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!overridden}
                              onClick={() => resetTarget(target.key)}
                              aria-label={`Reset ${target.label}`}
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button color="inherit" disabled={overriddenCount === 0} onClick={() => updatePage(() => ({}))}>
          Reset format ini
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Batal</Button>
        <Button
          variant="contained"
          onClick={() => onApply(sanitizeStyleOverrideMap(draft))}
          data-testid="lhu-style-override-apply"
        >
          Terapkan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
